// Package imports:
import React, { useEffect, useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
// Component imports:
import Toggle from "../../ui-elements/Toggle/Toggle";
import Input from "../../ui-elements/Input/Input";
import Button from "../../ui-elements/Button/Button";
// Service imports:
import { setPepComment, setPepFalsePositive, setPepReputationalComment, setPepReputationalFalsePositive } from "../../services/pepReviewApi";

export interface PepReviewUpdate {
    falsePositive?: boolean;
    comment?: string | null;
}

interface IProps {
    /** The pepReportId (the report row's id) to write back to. */
    pepReportId: number;
    /** Current whole-report false-positive verdict. */
    initialFalsePositive: boolean;
    /** Current reviewer comment, null when unset. */
    initialComment: string | null;
    /**
     * Called after a successful write so the parent can keep its copy of the report in sync
     * (e.g. for when the modal is closed and reopened without a refetch).
     */
    onReviewChange?: (reportId: number, update: PepReviewUpdate) => void;
    /** Which verdict this control writes: the international (default) or the reputational bucket. */
    bucket?: "international" | "reputational";
}

/**
 * Whole-report review controls shown at the bottom of the foreign PEP / sanctions match modals.
 *
 * - The false-positive flag is a Toggle that saves immediately on change (optimistic, reverts on error).
 * - The comment is free text with an explicit "Vista athugasemd" button; it is independent of the flag.
 *
 * The two writes hit independent endpoints (SetPepFalsePositive / SetPepComment).
 */
const PepReviewControls: React.FC<IProps> = ({
    pepReportId,
    initialFalsePositive,
    initialComment,
    onReviewChange,
    bucket = "international",
}) => {
    // Each bucket writes its own independent verdict via its own endpoints.
    const saveFalsePositive = bucket === "reputational" ? setPepReputationalFalsePositive : setPepFalsePositive;
    const saveComment = bucket === "reputational" ? setPepReputationalComment : setPepComment;

    const [falsePositive, setFalsePositive] = useState(initialFalsePositive);
    const [savingFlag, setSavingFlag] = useState(false);
    const [flagError, setFlagError] = useState<string | null>(null);

    // savedComment is the last value persisted on the server; comment is the editable draft.
    const [savedComment, setSavedComment] = useState(initialComment ?? "");
    const [comment, setComment] = useState(initialComment ?? "");
    const [savingComment, setSavingComment] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);
    const [commentSaved, setCommentSaved] = useState(false);

    const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (savedTimer.current) clearTimeout(savedTimer.current);
    }, []);

    const handleToggle = async (next: boolean) => {
        if (savingFlag) return;
        // Optimistic update - revert if the request fails.
        setFalsePositive(next);
        setSavingFlag(true);
        setFlagError(null);
        try {
            await saveFalsePositive(pepReportId, next);
            onReviewChange?.(pepReportId, { falsePositive: next });
        } catch (e) {
            setFalsePositive(!next);
            setFlagError(e instanceof Error ? e.message : "Ekki tókst að vista.");
            if (e instanceof Error) Bugsnag.notify(e);
        } finally {
            setSavingFlag(false);
        }
    };

    const commentDirty = comment !== savedComment;

    const handleSaveComment = async () => {
        if (savingComment || !commentDirty) return;
        const payload = comment.trim() === "" ? null : comment;
        setSavingComment(true);
        setCommentError(null);
        setCommentSaved(false);
        try {
            await saveComment(pepReportId, payload);
            setSavedComment(payload ?? "");
            onReviewChange?.(pepReportId, { comment: payload });
            setCommentSaved(true);
            if (savedTimer.current) clearTimeout(savedTimer.current);
            savedTimer.current = setTimeout(() => setCommentSaved(false), 4000);
        } catch (e) {
            setCommentError(e instanceof Error ? e.message : "Ekki tókst að vista athugasemd.");
            if (e instanceof Error) Bugsnag.notify(e);
        } finally {
            setSavingComment(false);
        }
    };

    return (
        <div
            className="KCL_pepReviewControls"
            style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: "20px", flexWrap: "wrap" }}
        >
            <div className="pepReviewControls__flag" style={{ flexShrink: 0, paddingTop: "8px" }}>
                <Toggle
                    toggled={falsePositive}
                    onToggle={handleToggle}
                    disabled={savingFlag}
                    label="Hafna niðurstöðu"
                />
                {flagError && (
                    <div className="input__error-message" style={{ marginTop: "6px" }}>{flagError}</div>
                )}
            </div>
            {falsePositive && (
                <div className="pepReviewControls__comment" style={{ flex: "1 1 280px", minWidth: 0 }}>
                    <Input
                        fullWidth
                        inputType="textArea"
                        id={`pep-comment-${pepReportId}`}
                        label="Athugasemd"
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            if (commentSaved) setCommentSaved(false);
                        }}
                        error={commentError ?? undefined}
                    />
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "12px",
                            marginTop: "10px",
                        }}
                    >
                        {commentSaved && (
                            <span className="paragraph-small" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <FontAwesomeIcon icon={faCheck} className="icon-yes" />
                                Athugasemd vistuð
                            </span>
                        )}
                        <Button
                            size="sm"
                            buttonType="secondary"
                            onClick={handleSaveComment}
                            disabled={!commentDirty || savingComment}
                            showLoader={savingComment}
                        >
                            Vista athugasemd
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PepReviewControls;
